import { useState, useEffect, useCallback } from "react";
import { Globe, Linkedin, Contact, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from '../../assets/images/SANS_EXPERT_logo_meca_gtech_fond_transparent.png';

const SocialIcons = () => (
      <div className="flex items-center space-x-3 social-media" >
        <a href="https://www.linkedin.com/company/meca-gtech/" target="blank_" className="text-dark-blue-600 hover:text-blue-700 transition-colors">
          <Linkedin size={16} />
        </a>

        <Link to="/contact" className="text-dark-blue-600 hover:text-blue-700 transition-colors">
          <Contact size={16} />
        </Link>
      </div>
);

const MenuItem = ({ to, children, subItems, isActive, onMouseEnter, onMouseLeave, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleClick = useCallback((e, path) => {
    e.preventDefault();
    
    // Si c'est un lien d'ancrage sur la même page
    if (path.includes('#')) {
      const [pathname, hash] = path.split('#');
      
      // Si on est déjà sur la bonne page, on fait défiler
      if (location.pathname === pathname || (pathname === '/' && location.pathname === '')) {
        const element = document.getElementById(hash);
        if (element) {
          const yOffset = -40; // Même marge que pour le chargement initial
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({top: y, behavior: 'smooth'});
          // Mise à jour de l'URL sans rechargement
          window.history.pushState(null, '', `#${hash}`);
        }
      } else {
        // Sinon, on navigue vers la page puis on fait défiler
        navigate(pathname, { 
          state: { scrollTo: hash },
          replace: true
        });
      }
    } else {
      // Pour les liens normaux
      navigate(path);
    }
    
    // Fermer le menu mobile si ouvert
    if (onClick) onClick();
  }, [navigate, location.pathname]);
  
  return (
    <div 
      className="menu-item-container"
      onMouseEnter={() => {
        setIsHovered(true);
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onMouseLeave?.();
      }}
      style={{ position: 'relative' }}
    >
      <Link 
        to={to}
        onClick={(e) => handleClick(e, to)}
        className={`nav-link ${isActive ? 'active' : ''}`}
        style={{ 
          textDecoration: 'none', 
          color: '#fff',
          background: 'rgba(103, 103, 104, 0.7)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          padding: '10px 20px',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          position: 'relative',
          fontWeight: '500',
          display: 'block',
          cursor: 'pointer',
          ':hover': {
            background: 'rgba(83, 83, 104, 0.8)'
          }
        }}
      >
        {children}
      </Link>
      
      {subItems && subItems.length > 0 && isHovered && (
        <div 
          className="submenu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '200px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '8px 0',
            marginTop: '2px',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}
        >
          {subItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              onClick={(e) => {
                const [pathname, hash] = item.path.split('#');
                handleClick(e, item.path);
                // Fermer le menu mobile après le clic
                if (onClick) onClick();
              }}
              style={{
                padding: '10px 20px',
                color: '#333',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                display: 'block',
                ':hover': {
                  background: 'rgba(0, 0, 0, 0.05)',
                  color: '#001322'
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [lang, setLang] = useState("fr");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const location = useLocation();
  
  // Données des menus et sous-menus
  const menuItems = [
    {
      title: 'Accueil',
      path: '/',
      subItems: [
        // { label: 'Présentation', path: '/#presentation' },
        // { label: 'Nos services', path: '/#services' },
        // { label: 'À propos', path: '/#a-propos' }
      ]
    },
    {
      title: 'Services',
      path: '/services',
      subItems: [
        // { label: 'Diagnostic technique', path: '/services#diagnostic' },
        // { label: 'Expertise', path: '/services#expertise' },
        // { label: 'Réparation', path: '/services#reparation' },
        // { label: 'Révision/Reconditionnement', path: '/services#revision' },
        // { label: 'Maintenance industrielle', path: '/services#maintenance' },
        // { label: 'Contrats d\'entretien', path: '/services#contrats-entretien' },
      ]
    },
    {
      title: 'Réalisations',
      path: '/achievements',  
      subItems: [
        // { label: 'Projets récents', path: '/achievements#projets' }
      ]
    },
    {
      title: 'Contact',
      path: '/contact',
      subItems: [
        // { label: 'Formulaire', path: '/contact#formulaire' }
      ]
    }
  ];

  const handleLangSwitch = () => {
    setLang((prev) => (prev === "fr" ? "en" : "fr"));
  };
  
  useEffect(() => {
    // Gestion du défilement après chargement de la page pour les ancres dans l'URL
    const scrollToWithOffset = (element) => {
      const yOffset = -80; // Ajustez cette valeur pour changer la marge supérieure
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    };

    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        scrollToWithOffset(element);
      }
    } else if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        scrollToWithOffset(element);
        // Nettoyer l'état pour éviter de re-scroller si l'utilisateur revient en arrière
        window.history.replaceState({}, document.title);
      }
    }
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  const isActive = (path) => {
    if (path === '/about' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease'
    }}>
      {/* Top mini bar */}
      <div
        style={{
          background: "#001322ff",
          padding: isScrolled ? "0 20px" : "5px 20px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          fontSize: "0.95em",
          height: isScrolled ? '0' : 'auto',
          maxHeight: isScrolled ? '0' : '40px',
          overflow: 'hidden',
          opacity: isScrolled ? 0 : 1,
          transition: 'all 0.3s ease'
        }}
      >
        <SocialIcons />
        <button
          style={{
            background: "#e0e0e0",
            border: "none",
            borderRadius: "12px",
            padding: "3px 10px",
            marginLeft: "1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
          onClick={handleLangSwitch}
          disabled
          title="Bientôt disponible"
        >
          <Globe size={16} />
          {lang === "fr" ? " EN" : " FR"}
        </button>
      </div>

      {/* Main menu bar */}
      <nav
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem 13%",
          background: isScrolled ? "rgba(255, 255, 255, 0.55)" : "rgb(255, 255, 255)",
          backdropFilter: isScrolled ? "blur(1rem)" : "blur(10px)",
          WebkitBackdropFilter: isScrolled ? "blur(1rem)" : "blur(10px)",
          boxShadow: isScrolled ? "0 2px 8px rgba(0,0,0,0.1)" : "0 2px 4px rgba(0,0,0,0.04)",
          transition: "all 0.3s ease"
        }}
      >
        {/* Logo */}
        <Link to="/" className="Menu-logo">
          <img src={Logo} alt="Logo_vers_accueil" width={"110vw"}/>
        </Link>

        {/* Hamburger button - visible only on mobile */}
        <button 
          className="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            color: "#011F37"
          }}
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Menu items */}
        <div 
          className="desktop-menu" 
          style={{ 
            display: "flex", 
            gap: "10px", 
            fontSize: "1.1em",
            position: 'relative'
          }}
        >
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              to={item.path}
              isActive={isActive(item.path) || (item.path === '/about' && location.pathname === '/')}
              subItems={item.subItems}
              onMouseEnter={() => setHoveredMenu(index)}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              {item.title}
            </MenuItem>
          ))}
        </div>

        {/* Mobile menu */}
        <div 
          className="mobile-menu"
          style={{
            display: "none",
            position: "fixed",
            top: isScrolled ? "60px" : "100px",
            left: 0,
            right: 0,
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "20px",
            flexDirection: "column",
            gap: "10px",
            maxHeight: isMobileMenuOpen ? "600px" : "0",
            overflow: "hidden",
            transition: "all 0.3s ease",
            opacity: isMobileMenuOpen ? 1 : 0,
            zIndex: 1000
          }}
        >
          {menuItems.map((item, index) => (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Link 
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  textDecoration: 'none', 
                  color: '#fff',
                  background: 'rgba(103, 103, 104, 0.7)',
                  backdropFilter: 'blur(5px)',
                  WebkitBackdropFilter: 'blur(5px)',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  fontWeight: '500',
                  textAlign: 'center'
                }}
              >
                {item.title}
              </Link>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                padding: '8px 0 0 20px',
                gap: '5px'
              }}>
                {item.subItems.map((subItem, subIndex) => (
                  <Link
                    key={subIndex}
                    to={subItem.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                      padding: '8px 15px',
                      color: '#333',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      background: 'rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>
    </header>
  );
};

export default Header;

// Styles globaux pour les animations
const globalStyles = document.createElement('style');
globalStyles.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .menu-item-container {
    position: relative;
  }
  
  .submenu {
    animation: fadeIn 0.2s ease-out;
  }
  
  .submenu a {
    display: block;
    color: #333;
    text-decoration: none;
    transition: all 0.2s ease;
  }
  
  .submenu a:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #001322;
  }
  
  @media (max-width: 768px) {
    .desktop-menu {
      display: none !important;
    }
    
    .mobile-menu {
      display: flex !important;
    }
    
    .mobile-menu-button {
      display: block !important;
    }
  }
`;

document.head.appendChild(globalStyles);